import { Owner, OwnerAccount, OwnerBasicDetails } from '@lib/types/owner';
import { Pet } from '@lib/types/pets';
import { SideEffects } from '@lib/types/shared';
import { callApi } from '@lib/utils/api';
import {
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

/**
 * Updates the basic details of a pet owner.
 * @param petOwnerId - The ID of the pet owner to update
 * @param basicDetails - The new basic details for the pet owner
 */
export async function updatePetOwnerBasicDetails(
  petOwnerId: string,
  basicDetails: OwnerBasicDetails,
  vetName?: string | null | undefined,
  vetContactPhoneNumber?: string | null | undefined
): Promise<void> {
  const updateData = {
    petOwnerId,
    request: basicDetails,
    vetName,
    vetContactPhoneNumber,
  };
  await callApi<void>('/api/rest/petOwners/petOwner/updateBasicInfo', {
    method: 'POST',
    body: updateData,
  });
}

interface CurrentPetOwnerAccountResponse {
  petOwnerAccount: OwnerAccount;
  petOwners: Owner[];
  pets: Pet[];
}

export const getCurrentPetOwnerAccount =
  async (): Promise<CurrentPetOwnerAccountResponse> => {
    const response = await callApi<CurrentPetOwnerAccountResponse>(
      '/api/rest/petOwners/currentPetOwnerAccount',
      { method: 'GET' }
    );
    return response;
  };

/**
 * Fetches current pet owner account with cache-busting for polling operations
 * Use this when you need guaranteed fresh data (e.g., in polling after mutations)
 */
export const getCurrentPetOwnerAccountFresh =
  async (): Promise<CurrentPetOwnerAccountResponse> => {
    const response = await callApi<CurrentPetOwnerAccountResponse>(
      `/api/rest/petOwners/currentPetOwnerAccount?t=${Date.now()}`,
      { method: 'GET' }
    );
    return response;
  };

interface PetResponse {
  id: string;
  name: string;
  breed?: {
    id: string;
    name: string;
  };
  petOwnerAccountId: string;
  profilePhotoUrl: string | null;
  profilePhotoFallbackUrl: string | null;
}

interface ApiResponse {
  value?: {
    petResponses?: PetResponse[];
  };
}

export interface PetOwnerCreatePayload {
  petOwnerAccountId: string | null | undefined;
  petOwnerRequest: {
    basicInfo: {
      firstName: string;
      surname: string;
      email?: string;
      phone: string;
      suburbId: string;
    };
    identityProviderId: string | null | undefined;
    photoUrl: string | null | undefined;
  };
  petRequests: Array<{
    name: string;
    birthdate: string | null | undefined;
    breedId: string;
    weightKg: number | null;
    heightCm?: number | null | undefined;
    genderKey?: string;
    desexed?: boolean;
    microchipNumber?: string | null | undefined;
    petInsuranceProvider?: string | null | undefined;
    petInsurancePolicyNumber?: string | null | undefined;
    petInsurancePolicyExpiryDate?: string | null | undefined;
    vaccinationCertificateUrls: string[] | null | undefined;
  }>;
  linkedToBusinesses: Array<{
    id: string;
  }>;
  termsAndConditionsChecked: boolean | null | undefined;
  initialPassword: string | null | undefined;
  skipSideEffects: SideEffects;
}

/**
 * Creates a pet owner and associated pets
 *
 * @param payload - Pet owner and pets information
 * @returns Promise resolving to created pets response
 */
export const createPetOwnerAndPets = async (
  payload: PetOwnerCreatePayload
): Promise<ApiResponse> => {
  return callApi<ApiResponse>(
    '/api/rest/petOwners/findOrCreatePetOwnerAndPet',
    {
      method: 'POST',
      body: payload,
    }
  );
};

export interface UpdatePetOwnerAccountPayload {
  petOwnerAccountId: string;
  petOwnerId: string;
  petOwnerRequest: {
    basicInfo: {
      firstName: string;
      surname: string;
      email?: string;
      phone: string;
      secondaryPhone?: string;
      suburbId: string;
      streetAddress?: string;
      addressInfo?: {
        fullAddress?: string;
        suburb: string;
        state: string;
        postcode?: string;
        country: string;
      };
    };
    identityProviderId: string | null | undefined;
    photoUrl: string | null | undefined;
    metadata?: Record<string, string> | null;
  };
  petRequests?: Array<{
    id?: string; // For existing pets
    name: string;
    birthdate?: string;
    breedId: string;
    desexed?: boolean;
    microchipNumber?: string | null;
    petInsuranceProvider?: string | null;
    petInsurancePolicyNumber?: string | null;
    petInsurancePolicyExpiryDate?: string | null;
    genderKey?: string;
    weightKg: number | null;
    heightCm?: number | null;
    instagramUsername?: string | null;
    vaccinationCertificateUrls?: string[] | null;
  }>;
  stripeCustomer?: {
    id: string;
    paymentMethod: {
      id: string;
      cardBrand: string;
      cardLast4: string;
      cardExpiryYear: number;
      cardExpiryMonth: number;
      cardBrandLogoUrl: string;
      cardStatus: {
        key: string;
        title: string;
      };
    };
  };
  termsAndConditionsChecked: boolean | null | undefined;
  designResearchOptIn?: boolean;
  newBusinessInMyArea?: boolean;
  specialOffersFromBusinesses?: boolean;
  petboostMeetUps?: boolean;
  smsAppointmentUpdates?: boolean;
  emailAppointmentUpdates?: boolean;
  appointmentReminderEmail?: boolean;
  appointmentReminderSms?: boolean;
  remindAboutWeeklyRecurringBookings?: boolean;
  vetName?: string;
  vetContactPhoneNumber?: string;
  // Business-specific terms and waiver acceptance
  businessTermsAccepted?: Array<{
    businessAccountId: string;
    url: string;
    timestampUtc: string;
  }>;
  businessWaiverAccepted?: Array<{
    businessAccountId: string;
    url: string;
    timestampUtc: string;
  }>;
  password: string | null | undefined;
  skipSideEffects?: SideEffects;
}

/**
 * Updates a pet owner account and associated pets
 *
 * @param payload - Pet owner and pets update information
 * @returns Promise resolving to updated pets response
 */
export const updatePetOwnerAccount = async (
  payload: UpdatePetOwnerAccountPayload
): Promise<ApiResponse> => {
  return callApi<ApiResponse>('/api/rest/petOwners/updatePetOwnerAccount', {
    method: 'POST',
    body: payload,
  });
};

// Stripe Setup Intent interface
interface SetupIntentResponse {
  setupIntentId: string;
  clientSecret: string;
}

/**
 * Generates a new Stripe setup intent for the current pet owner account
 * @param petOwnerAccountId - The ID of the pet owner account
 * @returns Promise resolving to setup intent information
 */
export const generateSetupIntent = async (
  petOwnerAccountId: string
): Promise<SetupIntentResponse> => {
  return callApi<SetupIntentResponse>(
    '/api/rest/petOwners/generateSetupIntent',
    {
      method: 'POST',
      body: { petOwnerAccountId },
    }
  );
};

/**
 * Detaches a payment method from the pet owner account
 * @param petOwnerAccountId - The ID of the pet owner account
 * @param paymentMethodId - The ID of the payment method to detach
 * @returns Promise resolving to success status
 */
export const detachPaymentMethod = async (
  petOwnerAccountId: string,
  paymentMethodId: string
): Promise<{ success: boolean }> => {
  return callApi<{ success: boolean }>(
    '/api/rest/petOwners/detachPaymentMethod',
    {
      method: 'POST',
      body: {
        petOwnerAccountId,
        paymentMethodId,
      },
    }
  );
};

/**
 * Sets a payment method as the default for the pet owner account
 * @param petOwnerAccountId - The ID of the pet owner account
 * @param paymentMethodId - The ID of the payment method to set as default
 * @returns Promise resolving to success status
 */
export const setDefaultPaymentMethod = async (
  petOwnerAccountId: string,
  paymentMethodId: string
): Promise<{ success: boolean }> => {
  return callApi<{ success: boolean }>(
    '/api/rest/petOwners/setDefaultPaymentMethod',
    {
      method: 'POST',
      body: {
        petOwnerAccountId,
        paymentMethodId,
      },
    }
  );
};

/**
 * Syncs payment methods from Stripe for the pet owner account
 * @param petOwnerAccountId - The ID of the pet owner account
 * @param waitForPaymentMethodId - Optional payment method ID to poll for until found
 * @returns Promise resolving to updated pet owner account data
 */
export const syncPaymentMethodsFromStripe = async (
  petOwnerAccountId: string,
  waitForPaymentMethodId?: string | null
): Promise<CurrentPetOwnerAccountResponse> => {
  return callApi<CurrentPetOwnerAccountResponse>(
    '/api/rest/petOwners/syncPaymentMethodsFromStripe',
    {
      method: 'POST',
      body: {
        petOwnerAccountId,
        waitForPaymentMethodId,
      },
    }
  );
};

/**
 * React Query hook to fetch the current pet owner account
 *
 * @param options - Query options
 * @returns UseQueryResult for the pet owner account data
 */
export const useCurrentPetOwnerAccount = (options?: {
  enabled?: boolean;
}): UseQueryResult<Awaited<ReturnType<typeof getCurrentPetOwnerAccount>>> => {
  const isEnabled = options?.enabled !== false; // Default to true if not specified

  return useQuery({
    queryKey: ['currentPetOwnerAccount'],
    queryFn: getCurrentPetOwnerAccount,
    enabled: isEnabled,
    // Keep previous data while fetching new data
    placeholderData: (previousData) => previousData,
    // Owner account data might change frequently, especially payment details
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Custom hook that provides a more convenient way to work with pet owner account data
 * including a dedicated function for forcing a refresh
 *
 * @param options - Hook options
 * @returns Object containing data, loading state, and a function to force refresh
 */
export const usePetOwnerAccountWithRefresh = (options?: {
  enabled?: boolean;
}) => {
  const queryResult = useCurrentPetOwnerAccount(options);
  const queryClient = useQueryClient();

  // Function to force a refresh of the pet owner account data
  const refreshPetOwnerAccount = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['currentPetOwnerAccount'],
    });
    return queryClient.refetchQueries({ queryKey: ['currentPetOwnerAccount'] });
  };

  return {
    data: queryResult.data,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refreshPetOwnerAccount,
  };
};
